import { useEffect, useState } from "react";
import { DatePicker, Spin } from "antd";
import { Button } from "src/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "src/components/ui/dialog";
import { Input } from "src/components/ui/input";
import { Switch } from "src/components/ui/switch";
import { PlusCircle, Edit, Trash2, RefreshCw } from "lucide-react";
import { DataTable } from "../../components/DataTable";
import { ArrowUpDown, MoreHorizontal, Pencil, Trash } from "lucide-react";
import { Badge } from "src/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenulabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { useLanguage } from "../../context/LanguageContext";
import { DateFormate } from "../../lib/utils";
import { Placeholder } from "@tiptap/extension-placeholder";
import MainButton from "../../components/MainButton";
import { DialogDescription } from "../../components/ui/dialog";
import LoaderPage from "../../components/LoaderPage";
import { BaseUrlApi, ErrorMessage } from "../../lib/api";
import axios from "axios";
import { toast } from "sonner";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

export default function PromoCodePage() {
  const { language } = useLanguage();

  const [errors, setErrors] = useState({});
  const [promoCodes, setPromoCodes] = useState([]);

  // PromoCodes
  const [loaders, setLoaders] = useState({
    getPromo: true,
    addPromo: false,
    editPromo: false,
    deletePromo: false,
  });

  const getAllPromoCodes = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/promocodes`);
      setPromoCodes(data.data);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoaders({ ...loaders, getPromo: false });
    }
  };

  useEffect(() => {
    getAllPromoCodes();
  }, []);

  const [openDialog, setOpenDialog] = useState(null);
  const [selectedPromo, setSelectedPromo] = useState(null);

  const handleAdd = async (newPromo) => {
    try {
      setErrors({});
      setLoaders((prev) => ({ ...prev, addPromo: true }));
      await axios.post(`${BaseUrlApi}/promocodes`, newPromo);
      setPromoCodes((prev) => [...prev, newPromo]);
      setOpenDialog(null);
      toast("Success", { description: "Promo code created successfully" });
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
      setErrors(result);
    } finally {
      setLoaders((prev) => ({ ...prev, addPromo: false }));
    }
  };

  const handleEdit = async (updatedPromo) => {
    try {
      setErrors({});
      setLoaders((prev) => ({ ...prev, editPromo: true }));
      await axios.put(
        `${BaseUrlApi}/promocodes/${selectedPromo.bid}`,
        updatedPromo
      );
      setPromoCodes((prev) =>
        prev.map((p) => (p.code === selectedPromo.code ? updatedPromo : p))
      );
      setOpenDialog(null);
      toast("Success", { description: "Promo code updated successfully" });
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
      setErrors(result);
    } finally {
      setLoaders((prev) => ({ ...prev, editPromo: false }));
    }
  };

  const handleDelete = async () => {
    if (!selectedPromo) return;

    try {
      setLoaders((prev) => ({ ...prev, deletePromo: true }));
      await axios.delete(`${BaseUrlApi}/promocodes/${selectedPromo.bid}`);
      setPromoCodes((prev) =>
        prev.filter((p) => p.code !== selectedPromo.code)
      );
      setOpenDialog(null);
      toast("Success", { description: "Promo code deleted successfully" });
    } catch (error) {
      toast("Error", { description: ErrorMessage(error) });
    } finally {
      setLoaders((prev) => ({ ...prev, deletePromo: false }));
    }
  };

  const handleEditClick = (promo) => {
    setSelectedPromo({ ...promo });
    setOpenDialog("edit");
  };

  const handleDeleteClick = (promo) => {
    setSelectedPromo({ ...promo });
    setOpenDialog("delete");
  };

  const columns = [
    {
      accessorKey: "code",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="p-0 hover:bg-transparent"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Promo Code
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
    },
    {
      accessorKey: "name",
      header: "Name",
    },
    {
      accessorKey: "max_uses",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="p-0 hover:bg-transparent"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Max Uses
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
    },
    {
      accessorKey: "max_uses_user",
      header: "Uses Per User",
    },
    {
      accessorKey: "discount_amount",
      header: "Discount",
      cell: ({ row }) => {
        const discount = Number.parseFloat(row.getValue("discount_amount"));
        const isFixed = row.original.is_fixed;

        const formatted = isFixed
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(discount)
          : `${discount}%`;

        return <div className="font-medium">{formatted}</div>;
      },
    },
    {
      accessorKey: "validity",
      header: "Validity Period",
      cell: ({ row }) => {
        const start = DateFormate(row.original.starts_at);
        const end = DateFormate(row.original.expires_at);
        return (
          <div>
            {start} - {end}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const now = new Date();
        const start = new Date(row.original.starts_at);
        const end = new Date(row.original.expires_at);

        const status =
          now < start ? "Upcoming" : now > end ? "Expired" : "Active";

        const variant =
          status === "Active"
            ? "default"
            : status === "Upcoming"
            ? "secondary"
            : "destructive";

        return <Badge variant={variant}>{status}</Badge>;
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const promo = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => navigator.clipboard.writeText(promo.code)}
              >
                Copy Promo Code
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleEditClick(promo)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDeleteClick(promo)}>
                <Trash className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Promo Codes</h1>

        <MainButton
          Icon={PlusCircle}
          title={"Add Promo"}
          onClick={() => setOpenDialog("add")}
        />
      </div>

      {loaders.getPromo ? (
        <div className="flex items-center justify-center h-40">
          <Spin size="large" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={promoCodes}
          title={"Promo code by code, name"}
          filterNames={["name", "code"]}
        />
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog === "add" || openDialog === "edit"}
        onOpenChange={(open) => !open && setOpenDialog(null)}
      >
        <DialogContent
          className="sm:max-w-[600px]"
          dir={language === "ar" ? "rtl" : "ltr"}
        >
          <DialogHeader>
            <DialogTitle>
              {openDialog === "add" ? "Add" : "Edit"} Promo Code
            </DialogTitle>
          </DialogHeader>
          <PromoForm
            errors={errors}
            loaders={loaders}
            initialData={openDialog === "edit" ? selectedPromo : undefined}
            onSubmit={openDialog === "add" ? handleAdd : handleEdit}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={openDialog === "delete"}
        onOpenChange={(open) => !open && setOpenDialog(null)}
      >
        <DialogContent
          className="sm:max-w-[425px]"
          dir={language === "ar" ? "rtl" : "ltr"}
        >
          <DialogHeader>
            <DialogTitle>Delete Promo Code</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <strong>{selectedPromo?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={loaders.deletePromo}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PromoForm({ initialData, onSubmit, loaders, errors }) {
  const [formData, setFormData] = useState({
    bid: initialData?.bid || 0,
    code: initialData?.code || "",
    name: initialData?.name || "",
    description: initialData?.description || "",
    max_uses: initialData?.max_uses || 0,
    max_uses_user: initialData?.max_uses_user || 0,
    discount_amount: initialData?.discount_amount || 0,
    is_fixed: initialData?.is_fixed || false,
    starts_at: initialData?.starts_at || "",
    expires_at: initialData?.expires_at || "",
  });

  const [dates, setDates] = useState(
    initialData
      ? [dayjs(initialData.starts_at), dayjs(initialData.expires_at)]
      : null
  );

  const handleDateChange = (dates) => {
    setDates(dates);
    if (dates && dates.length === 2) {
      setFormData({
        ...formData,
        starts_at: dates[0].format("YYYY-MM-DD HH:mm:ss"),
        expires_at: dates[1].format("YYYY-MM-DD HH:mm:ss"),
      });
    }
  };

  const generateRandomCode = () => {
    const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return code;
  };

  return (
    <div className="grid gap-4">
      {/* Code Field */}
      <div className="grid grid-cols-4 items-center gap-4">
        <label htmlFor="code" className="text-start">
          Code
        </label>
        <div className="col-span-3 flex items-center gap-2">
          <Input
            id="code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            placeholder="Enter promo code or generate"
          />
          <Button
            type="button"
            size="sm"
            onClick={() => {
              const newCode = generateRandomCode();
              setFormData({ ...formData, code: newCode });
            }}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Name Field */}
      <div className="grid grid-cols-4 items-center gap-4">
        <label htmlFor="name" className="text-start">
          Name
        </label>
        <div className="col-span-3 flex flex-col gap-1">
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
            }}
            placeholder="Enter promo name"
          />
          {errors?.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name[0]}</p>
          )}
        </div>
      </div>

      {/* Description Field */}
      <div className="grid grid-cols-4 items-center gap-4">
        <label htmlFor="description" className="text-start">
          Description
        </label>
        <div className="col-span-3 flex flex-col gap-1">
          <Input
            id="description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Enter promo description"
          />
        </div>
      </div>

      {/* Validity Field */}
      <div className="grid grid-cols-4 items-center gap-4">
        <label className="text-start">Validity</label>
        <div className="col-span-3 flex flex-col gap-1">
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            onChange={handleDateChange}
            value={dates}
            className="w-full"
          />
          {errors?.validity && (
            <p className="text-red-500 text-sm mt-1">{errors.validity[0]}</p>
          )}
        </div>
      </div>

      {/* Discount Amount Field */}
      <div className="grid grid-cols-4 items-center gap-4">
        <label htmlFor="discount" className="text-start">
          Discount Amount
        </label>
        <div className="col-span-3 flex flex-col gap-1">
          <Input
            id="discount"
            type="number"
            value={formData.discount_amount}
            onChange={(e) => {
              setFormData({
                ...formData,
                discount_amount: Number(e.target.value),
              });
            }}
          />
          {errors?.discount_amount && (
            <p className="text-red-500 text-sm mt-1">
              {errors.discount_amount[0]}
            </p>
          )}
        </div>
      </div>

      {/* Fixed Amount Switch */}
      <div className="grid grid-cols-4 items-center gap-4">
        <label htmlFor="fixed" className="text-start">
          Fixed Amount
        </label>
        <Switch
          checked={formData.is_fixed}
          onCheckedChange={(checked) =>
            setFormData({ ...formData, is_fixed: checked })
          }
        />
      </div>

      {/* Max Uses Field */}
      <div className="grid grid-cols-4 items-center gap-4">
        <label htmlFor="max_uses" className="text-start">
          Max Uses
        </label>
        <div className="col-span-3 flex flex-col gap-1">
          <Input
            id="max_uses"
            type="number"
            value={formData.max_uses}
            onChange={(e) => {
              setFormData({ ...formData, max_uses: Number(e.target.value) });
            }}
          />
          {errors?.max_uses && (
            <p className="text-red-500 text-sm mt-1">{errors.max_uses[0]}</p>
          )}
        </div>
      </div>

      {/* Max Uses Per User Field */}
      <div className="grid grid-cols-4 items-center gap-4">
        <label htmlFor="max_uses_user" className="text-start">
          Max Uses Per User
        </label>
        <div className="col-span-3 flex flex-col gap-1">
          <Input
            id="max_uses_user"
            type="number"
            value={formData.max_uses_user}
            onChange={(e) => {
              setFormData({
                ...formData,
                max_uses_user: Number(e.target.value),
              });
            }}
          />
          {errors?.max_uses_user && (
            <p className="text-red-500 text-sm mt-1">
              {errors.max_uses_user[0]}
            </p>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button
          type="submit"
          onClick={() => onSubmit(formData)}
          disabled={loaders.addPromo || loaders.editPromo}
        >
          {initialData ? "Save Changes" : "Create Promo"}
        </Button>
      </DialogFooter>
    </div>
  );
}
